/**
 * AdminFinance — the "Finance" sidebar group.
 *
 * Three pages:
 *   1. AdminFinanceRevenue  — combined KPI snapshot (Enterprise/Subscriber
 *      contracts + oodel SCORE invoices).
 *   2. AdminFinanceInvoices — cross-business oodel SCORE invoice rollup
 *      (previously only viewable one business at a time, on the business's
 *      own detail page — this is the first cross-business view).
 *   3. AdminFinancePayments — Enterprise/Subscriber customer payment status,
 *      reusing the existing /admin/customers + /admin/revenue data that used
 *      to live as 2 stray cards on the generic Dashboard.
 *
 * All 3 use the lighter, rounded "world-class" visual language (matches the
 * Patron dashboard) rather than the older black-border boxy style used
 * elsewhere in Admin.jsx — this is the new default for admin going forward.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { CheckCircle, Clock, FileText } from "@phosphor-icons/react";

function FinanceHeader({ title, subtitle }) {
  return (
    <div className="mb-7">
      <div className="text-[11px] font-bold tracking-widest text-mango uppercase">Finance</div>
      <h1 className="text-3xl font-black tracking-tight mt-0.5">{title}</h1>
      {subtitle && <p className="text-sm text-neutral-500 mt-1.5 max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function Kpi({ label, value, tone = "default", testid }) {
  const toneCls = tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : "text-neutral-900";
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid={testid}>
      <div className="text-[10px] font-bold tracking-wider text-neutral-400 uppercase">{label}</div>
      <div className={`text-2xl font-black mt-1.5 ${toneCls}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: "bg-emerald-50 text-emerald-600",
    pending: "bg-amber-50 text-amber-600",
    overdue: "bg-red-50 text-red-600",
  };
  return <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${map[status] || "bg-neutral-100 text-neutral-500"}`}>{(status || "—").toUpperCase()}</span>;
}

const money = (n) => `Rs ${Math.round(n || 0).toLocaleString()}`;

// ============================================================ 1. Overview
export function AdminFinanceRevenue() {
  const [revenue, setRevenue] = useState(() => api.getCached("/admin/revenue"));
  const [invoices, setInvoices] = useState(() => api.getCached("/admin/cs/invoices"));
  useEffect(() => {
    api.get("/admin/revenue").then((r) => setRevenue(r.data)).catch(() => toast.error("Could not load revenue"));
    api.get("/admin/cs/invoices").then((r) => setInvoices(r.data)).catch(() => toast.error("Could not load invoices"));
  }, []);
  if (!revenue || !invoices) return <div className="p-10 text-neutral-400 text-sm">Loading…</div>;
  const combinedAccrued = revenue.total_accrued + invoices.total_paid + invoices.total_pending;
  const combinedPaid = revenue.total_paid + invoices.total_paid;
  const combinedPending = revenue.total_pending + invoices.total_pending;
  return (
    <div className="max-w-6xl" data-testid="admin-finance-revenue-page">
      <FinanceHeader title="Revenue Overview" subtitle="Combines Enterprise & Subscriber contract billing with oodel SCORE invoicing — previously scattered across the generic Dashboard and each business's own detail page." />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <Kpi label="Total accrued (combined)" value={money(combinedAccrued)} testid="finance-kpi-accrued" />
        <Kpi label="Total paid (combined)" value={money(combinedPaid)} tone="green" testid="finance-kpi-paid" />
        <Kpi label="Pending / overdue (combined)" value={money(combinedPending)} tone="amber" testid="finance-kpi-pending" />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-bold">Enterprise &amp; Subscriber contracts</div>
            <Link to="/admin/finance-payments" className="text-xs font-semibold text-mango hover:underline">View all →</Link>
          </div>
          <p className="text-xs text-neutral-400 mb-4">{revenue.enterprise_count} Enterprise · {revenue.subscriber_count} Subscriber accounts</p>
          <div className="space-y-2 text-sm">
            {Object.entries(revenue.by_status).map(([status, amt]) => (
              <div key={status} className="flex items-center justify-between py-1.5 border-b border-neutral-50 last:border-0">
                <StatusPill status={status} />
                <span className="font-mono font-semibold">{money(amt)}</span>
              </div>
            ))}
            {Object.keys(revenue.by_status).length === 0 && <div className="text-neutral-400 text-sm">No contracts yet.</div>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-bold">oodel SCORE invoices</div>
            <Link to="/admin/finance-invoices" className="text-xs font-semibold text-mango hover:underline">View all →</Link>
          </div>
          <p className="text-xs text-neutral-400 mb-4">{invoices.count} invoices across all businesses</p>
          <div className="space-y-2 text-sm">
            {invoices.invoices.slice(0, 5).map((inv) => (
              <div key={inv.id} className="flex items-center justify-between py-1.5 border-b border-neutral-50 last:border-0">
                <span className="truncate max-w-[160px]">{inv.business_name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono text-xs text-neutral-500">{money(inv.amount)}</span>
                  <StatusPill status={inv.status} />
                </div>
              </div>
            ))}
            {invoices.invoices.length === 0 && <div className="text-neutral-400 text-sm">No invoices generated yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================ 2. Invoices
export function AdminFinanceInvoices() {
  const [data, setData] = useState(() => api.getCached("/admin/cs/invoices"));
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/cs/invoices").then((r) => setData(r.data)).catch(() => toast.error("Could not load invoices"));
  useEffect(() => { load(); }, []);
  const markPaid = async (iid) => {
    try { await api.put(`/admin/cs/invoices/${iid}/mark-paid`); toast.success("Marked paid"); load(); }
    catch { toast.error("Failed to update invoice"); }
  };
  const markPending = async (iid) => {
    try { await api.put(`/admin/cs/invoices/${iid}/mark-pending`); toast.success("Marked pending"); load(); }
    catch { toast.error("Failed to update invoice"); }
  };
  if (!data) return <div className="p-10 text-neutral-400 text-sm">Loading…</div>;
  const rows = data.invoices.filter((i) => filter === "all" || i.status === filter);
  return (
    <div className="max-w-6xl" data-testid="admin-finance-invoices-page">
      <FinanceHeader title="oodel SCORE Invoices" subtitle="Every invoice across every oodel SCORE business, newest first." />
      <div className="flex gap-2 mb-5">
        {["all", "paid", "pending"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`finance-invoices-filter-${f}`}
            className={`text-xs font-bold px-4 py-2 rounded-full transition-colors ${filter === f ? "bg-mango text-white" : "bg-white border border-neutral-200 text-neutral-500 hover:border-neutral-400"}`}
          >
            {f === "all" ? "All" : f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-neutral-400 uppercase tracking-wide border-b border-neutral-100">
              <th className="py-3 px-5">Business</th>
              <th className="py-3 px-5">Period</th>
              <th className="py-3 px-5">Amount</th>
              <th className="py-3 px-5">Status</th>
              <th className="py-3 px-5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((inv) => (
              <tr key={inv.id} className="border-b border-neutral-50 last:border-0" data-testid={`finance-invoice-row-${inv.id}`}>
                <td className="py-3 px-5">
                  <Link to={`/admin/customer-sentiment/${inv.business_id}`} className="font-semibold hover:text-mango">{inv.business_name}</Link>
                </td>
                <td className="py-3 px-5 text-neutral-500 text-xs">{inv.period_start?.slice(0, 10)} → {inv.period_end?.slice(0, 10)}</td>
                <td className="py-3 px-5 font-mono">{money(inv.amount)}</td>
                <td className="py-3 px-5"><StatusPill status={inv.status} /></td>
                <td className="py-3 px-5 text-right">
                  {inv.status === "paid" ? (
                    <button onClick={() => markPending(inv.id)} className="text-xs text-neutral-400 hover:text-black" data-testid={`finance-invoice-mark-pending-${inv.id}`}>Mark pending</button>
                  ) : (
                    <button onClick={() => markPaid(inv.id)} className="text-xs font-semibold text-mango hover:underline" data-testid={`finance-invoice-mark-paid-${inv.id}`}>Mark paid</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-center text-sm text-neutral-400 py-12">No invoices in this filter.</div>}
      </div>
    </div>
  );
}

// ============================================================ 3. Payments
export function AdminFinancePayments() {
  const [customers, setCustomers] = useState(() => api.getCached("/admin/customers"));
  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => toast.error("Could not load customers"));
  }, []);
  if (!customers) return <div className="p-10 text-neutral-400 text-sm">Loading…</div>;
  // Pending/overdue first — that's what admin needs to chase.
  const order = { overdue: 0, pending: 1, paid: 2 };
  const sorted = [...customers].sort((a, b) => (order[a.payment_status] ?? 3) - (order[b.payment_status] ?? 3));
  return (
    <div className="max-w-6xl" data-testid="admin-finance-payments-page">
      <FinanceHeader title="Enterprise & Subscriber Payments" subtitle="Payment status for every business customer contract, pending/overdue surfaced first. Edit contract details from Audience & Customers → Business Customers." />
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-neutral-400 uppercase tracking-wide border-b border-neutral-100">
              <th className="py-3 px-5">Company</th>
              <th className="py-3 px-5">Tier</th>
              <th className="py-3 px-5">Annual fee</th>
              <th className="py-3 px-5">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id} className="border-b border-neutral-50 last:border-0" data-testid={`finance-payment-row-${c.id}`}>
                <td className="py-3 px-5 font-semibold">{c.name || c.email}</td>
                <td className="py-3 px-5 text-neutral-500 capitalize">{c.subscription_tier || "—"}</td>
                <td className="py-3 px-5 font-mono">{money(c.annual_fee)}</td>
                <td className="py-3 px-5"><StatusPill status={c.payment_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {sorted.length === 0 && <div className="text-center text-sm text-neutral-400 py-12">No business customers yet.</div>}
      </div>
    </div>
  );
}
